import { notification } from 'features/notifications/toast/notification';
import { logger } from 'utils/logger';
import { store } from 'store/store';
import {
  connectionQualityLevelChanged,
  indicatorAverageMOSChanged,
  weakConnectionActiveChanged,
} from 'features/system-stats/systemStatsSlice';
import ConnectionQualityNotification from 'features/system-stats/ConnectionQualityNotification';
import { WEAK_CONNECTION_NOTIFICATION_ID } from 'features/notifications/constants';
import { selectConnectionMessageEnabled } from 'features/application/applicationSlice';
import { selectLocalVideoBroadcasting } from 'features/streaming/streamingSlice';

const READING_INTERVAL_SECONDS = 5;
const READING_INTERVAL_MS = READING_INTERVAL_SECONDS * 1000;

const MOS_BUFFER_SIZE = 10;
const MIN_SAMPLES_FOR_AVERAGE = 3;
const AVERAGE_TREND_WINDOW = 3;

const WARMUP_PERIOD_SECONDS = 30;
const REQUIRED_POOR_READINGS_WARMUP = 4;
const REQUIRED_POOR_READINGS_NORMAL = 3;
const REQUIRED_GOOD_READINGS_FOR_DISMISSAL = 2;

// ~6s at 2s cadence, just over one hysteresis tick — blips don't drain, sustained loss does.
const EMPTY_TICKS_BEFORE_DRAIN = 3;

export const MOS_THRESHOLDS = {
  /** Below this MOS the connection is considered "bad" — lower bound of poor. */
  poor: 2,
  fair: 3,
  good: 4,
} as const;

export enum ConnectionQualityLevel {
  poor = 0,
  fair = 1,
  good = 2,
}

// Dead-band margin: leaving a level requires clearing its boundary by this;
// entering does not. Suppresses boundary-hover flapping. Single tunable.
const LEVEL_HYSTERESIS_MARGIN = 0.3;

const rawLevel = (mos: number): ConnectionQualityLevel => {
  if (mos < MOS_THRESHOLDS.fair) {
    return ConnectionQualityLevel.poor;
  }
  if (mos < MOS_THRESHOLDS.good) {
    return ConnectionQualityLevel.fair;
  }
  return ConnectionQualityLevel.good;
};

// Exported for O7 per-tile reuse. previous === null (cold start / post-drain)
// maps with no directional bias.
export const deriveQualityLevel = (
  mos: number,
  previous: ConnectionQualityLevel | null
): ConnectionQualityLevel => {
  if (previous === null) {
    return rawLevel(mos);
  }
  const H = LEVEL_HYSTERESIS_MARGIN;
  if (previous === ConnectionQualityLevel.good) {
    return mos < MOS_THRESHOLDS.good - H ? rawLevel(mos) : ConnectionQualityLevel.good;
  }
  if (previous === ConnectionQualityLevel.poor) {
    return mos >= MOS_THRESHOLDS.fair + H ? rawLevel(mos) : ConnectionQualityLevel.poor;
  }
  if (mos >= MOS_THRESHOLDS.good + H) {
    return ConnectionQualityLevel.good;
  }
  if (mos < MOS_THRESHOLDS.fair - H) {
    return ConnectionQualityLevel.poor;
  }
  return ConnectionQualityLevel.fair;
};

const pushBounded = <T>(buffer: T[], value: T, maxSize: number): void => {
  buffer.push(value);
  if (buffer.length > maxSize) {
    buffer.shift();
  }
};

const mean = (values: readonly number[]): number =>
  values.reduce((sum, v) => sum + v, 0) / values.length;

export interface QualityEvaluation {
  // Smoothed indicator signal — drives the level dot and diagnostic panel.
  averageMOS: number;
  // Smoothed worstPublishing — drives the toast gate. Null until the publishing buffer fills.
  publishingAverageMOS: number | null;
  level: ConnectionQualityLevel;
  isImproving: boolean;
  inWarmup: boolean;
}

export interface TickSample {
  // Signal the level dot reads — min(all tracks), gated by connectionQualityV2Enabled.
  indicatorMOS: number;
  // worstPublishing for this tick. Null on listener-only ticks or warmup gaps;
  // kept separate from indicatorMOS so the toast gate reads a clean uplink signal.
  publishingMOS: number | null;
}

export class ConnectionQuality {
  private qualityTimer?: number;

  // Drives the level dot and the smoothed averageMOS shown in the diagnostic panel.
  private readonly indicatorSamples: number[] = [];

  // Drives the toast gate. Decoupled from indicatorSamples so a bad remote feed
  // can't trigger a broadcaster's "weak connection" toast.
  private readonly publishingSamples: number[] = [];

  // Trend only feeds the toast decision — the level dot doesn't use it.
  private readonly recentPublishingAverages: number[] = [];

  // Null until first observed, so cold start/drain can't fake an "improving" verdict.
  private previousPublishingAverage: number | null = null;

  private goodReadingsCounter = 0;

  private consecutivePoorReadings = 0;

  private readingDurationSeconds = 0;

  private consecutiveEmptyTicks = 0;

  // Previous tick's level, feeding the dead-band. Null on cold start/after a drain.
  private currentLevel: ConnectionQualityLevel | null = null;

  recordSample = (sample: TickSample): void => {
    this.consecutiveEmptyTicks = 0;
    pushBounded(this.indicatorSamples, sample.indicatorMOS, MOS_BUFFER_SIZE);
    if (sample.publishingMOS !== null) {
      pushBounded(this.publishingSamples, sample.publishingMOS, MOS_BUFFER_SIZE);
    }
  };

  recordEmptyTick = (): void => {
    this.consecutiveEmptyTicks += 1;
    if (this.consecutiveEmptyTicks < EMPTY_TICKS_BEFORE_DRAIN) {
      return;
    }
    if (this.indicatorSamples.length === 0 && this.publishingSamples.length === 0) {
      return;
    }
    this.indicatorSamples.length = 0;
    this.publishingSamples.length = 0;
    this.recentPublishingAverages.length = 0;
    this.previousPublishingAverage = null;
    this.goodReadingsCounter = 0;
    this.consecutivePoorReadings = 0;
    this.currentLevel = null;
    store.dispatch(connectionQualityLevelChanged(null));
    store.dispatch(indicatorAverageMOSChanged(null));
    store.dispatch(weakConnectionActiveChanged(false));
  };

  evaluateOnce = (): QualityEvaluation | null => {
    if (this.indicatorSamples.length < MIN_SAMPLES_FOR_AVERAGE) {
      return null;
    }

    const averageMOS = mean(this.indicatorSamples);
    const publishingAverageMOS =
      this.publishingSamples.length >= MIN_SAMPLES_FOR_AVERAGE
        ? mean(this.publishingSamples)
        : null;
    const isImproving = this.updateTrendAndCheckImproving(publishingAverageMOS);

    return {
      averageMOS,
      publishingAverageMOS,
      level: deriveQualityLevel(averageMOS, this.currentLevel),
      isImproving,
      inWarmup: this.readingDurationSeconds < WARMUP_PERIOD_SECONDS,
    };
  };

  startHysteresisLoop = (): void => {
    this.qualityTimer = window.setInterval(() => {
      this.readingDurationSeconds += READING_INTERVAL_SECONDS;

      const evaluation = this.evaluateOnce();
      if (evaluation === null) {
        return;
      }

      store.dispatch(connectionQualityLevelChanged(evaluation.level));
      this.currentLevel = evaluation.level;
      store.dispatch(indicatorAverageMOSChanged(evaluation.averageMOS));

      if (!selectConnectionMessageEnabled(store.getState())) {
        return;
      }

      // Toast fires off the publishing-side signal only. No signal yet (listener,
      // warmup gap) → do nothing, neither fire nor dismiss.
      if (evaluation.publishingAverageMOS === null) {
        return;
      }

      const requiredReadings = evaluation.inWarmup
        ? REQUIRED_POOR_READINGS_WARMUP
        : REQUIRED_POOR_READINGS_NORMAL;

      if (evaluation.publishingAverageMOS < MOS_THRESHOLDS.fair) {
        this.handlePoorQuality(evaluation, requiredReadings);
      } else {
        this.handleGoodQuality(evaluation.publishingAverageMOS);
      }
    }, READING_INTERVAL_MS);
  };

  stopHysteresisLoop = (): void => {
    if (this.qualityTimer) {
      window.clearInterval(this.qualityTimer);
      this.qualityTimer = undefined;
    }
    this.currentLevel = null;
    store.dispatch(connectionQualityLevelChanged(null));
    store.dispatch(indicatorAverageMOSChanged(null));
    store.dispatch(weakConnectionActiveChanged(false));
  };

  private updateTrendAndCheckImproving = (currentAverage: number | null): boolean => {
    if (currentAverage === null) {
      return false;
    }
    pushBounded(this.recentPublishingAverages, currentAverage, AVERAGE_TREND_WINDOW);

    let trendImproving = false;
    if (this.recentPublishingAverages.length >= AVERAGE_TREND_WINDOW) {
      const firstValue = this.recentPublishingAverages[0];
      const lastValue = this.recentPublishingAverages[this.recentPublishingAverages.length - 1];
      trendImproving = lastValue > firstValue;
    }

    const isLatestBetter =
      this.previousPublishingAverage !== null && currentAverage > this.previousPublishingAverage;
    this.previousPublishingAverage = currentAverage;
    return isLatestBetter || trendImproving;
  };

  private handlePoorQuality = (evaluation: QualityEvaluation, requiredReadings: number): void => {
    this.goodReadingsCounter = 0;
    this.consecutivePoorReadings += 1;

    const sufficientPoorReadings = this.consecutivePoorReadings >= requiredReadings;
    if (sufficientPoorReadings && !evaluation.isImproving) {
      // evaluation.publishingAverageMOS is non-null here — the caller already
      // gated on it, but TypeScript doesn't narrow across the call boundary.
      this.showWarningNotification(evaluation.publishingAverageMOS ?? evaluation.averageMOS);
    }
  };

  private handleGoodQuality = (publishingAverageMOS: number): void => {
    this.consecutivePoorReadings = 0;
    this.goodReadingsCounter += 1;

    if (this.goodReadingsCounter >= REQUIRED_GOOD_READINGS_FOR_DISMISSAL) {
      this.dismissWarningNotification(publishingAverageMOS);
    }
  };

  private showWarningNotification = (publishingAverageMOS: number): void => {
    if (!selectLocalVideoBroadcasting(store.getState())) {
      return;
    }

    store.dispatch(weakConnectionActiveChanged(true));
    notification(ConnectionQualityNotification, {
      position: 'top-center',
      closeOnClick: false,
      autoClose: false,
      toastId: WEAK_CONNECTION_NOTIFICATION_ID,
      className: 'weak-connection-toast',
    });

    logger
      .remote()
      .warn(
        `Connection is weak. Publishing MOS: ${publishingAverageMOS.toFixed(2)}, Consecutive poor readings: ${this.consecutivePoorReadings}`
      );
  };

  private dismissWarningNotification = (publishingAverageMOS: number): void => {
    store.dispatch(weakConnectionActiveChanged(false));
    if (notification.isActive(WEAK_CONNECTION_NOTIFICATION_ID)) {
      notification.dismiss(WEAK_CONNECTION_NOTIFICATION_ID);
      logger
        .remote()
        .info(`Connection quality recovered. Publishing MOS: ${publishingAverageMOS.toFixed(2)}`);
    }
  };
}
